import { useQueryClient } from '@tanstack/react-query';
import { NotificationManager } from 'react-notifications';
import { useApiMutation } from '@/hooks/api/useApiMutation';
import { useGetQuery } from '@/hooks/api/useGetQuery';
import { queryKeys } from '@/queries/query-keys';
import { useAuthStore } from '@/stores/auth-store';

/** GET `/org/members` — team accounts for the current organization. */
export function useOrgMembersQuery() {
  return useGetQuery(() => '/org/members', {}, queryKeys.orgMembers(), {
    staleTime: 30_000,
  });
}

/** PUT `/admin/account` — update the signed-in user's profile fields. */
export function useUpdateAccountMutation() {
  const queryClient = useQueryClient();

  return useApiMutation(
    () => '/admin/account',
    'put',
    {},
    {
      onSuccess: (data) => {
        useAuthStore.getState().setSession(data);
        queryClient.invalidateQueries({ queryKey: queryKeys.session() });
      },
      onError: (err) => {
        NotificationManager.error(
          err.message || 'Could not update account',
          '',
          4000,
        );
      },
    },
    (values) => ({
      firstName: values.firstName,
      lastName: values.lastName,
      accountName: values.accountName,
      phoneNumber: values.phoneNumber ?? null,
    }),
  );
}

/** PUT `/org` — update organization details (ADMIN only on server). */
export function useUpdateOrgMutation() {
  const queryClient = useQueryClient();

  return useApiMutation(
    () => '/org',
    'put',
    {},
    {
      onSuccess: (org) => {
        const current = useAuthStore.getState().currentUser;
        if (current?.organization) {
          useAuthStore.getState().setSession({
            ...current,
            organization: { ...current.organization, ...org },
          });
        }
        queryClient.invalidateQueries({ queryKey: queryKeys.session() });
      },
      onError: (err) => {
        NotificationManager.error(
          err.message || 'Could not update organization',
          '',
          4000,
        );
      },
    },
    (values) => ({
      orgId: values.orgId,
      orgName: values.orgName,
      emailAddress: values.emailAddress ?? null,
      websiteAddress: values.websiteAddress ?? null,
      orgPhoto: values.orgPhoto ?? null,
    }),
  );
}

/**
 * POST `/org/member` — invite/add team member (ADMIN).
 * Server generates password and emails the member.
 */
export function useAddOrgMemberMutation() {
  const queryClient = useQueryClient();

  return useApiMutation(
    () => '/org/member',
    'post',
    {},
    {
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: queryKeys.orgMembers() });
        NotificationManager.success('Team member added', '', 3000);
      },
      onError: (err) => {
        NotificationManager.error(
          err.message || 'Could not add team member',
          '',
          4000,
        );
      },
    },
    (values) => ({
      accountName: values.accountName.trim(),
      firstName: values.firstName.trim(),
      lastName: values.lastName.trim(),
      phoneNumber: values.phoneNumber?.trim() || null,
      role: values.role,
    }),
  );
}

/** PUT `/org/member` — change a member's role (ADMIN; not admin accounts). */
export function useUpdateOrgMemberMutation() {
  const queryClient = useQueryClient();

  return useApiMutation(
    () => '/org/member',
    'put',
    {},
    {
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: queryKeys.orgMembers() });
        NotificationManager.success('Team member updated', '', 3000);
      },
      onError: (err) => {
        NotificationManager.error(
          err.message || 'Could not update team member',
          '',
          4000,
        );
      },
    },
    (values) => ({
      accountName: values.accountName,
      firstName: values.firstName.trim(),
      lastName: values.lastName.trim(),
      role: values.role,
    }),
  );
}

/** DELETE `/org/member/{accountName}` — remove team member (ADMIN). */
export function useDeleteOrgMemberMutation() {
  const queryClient = useQueryClient();

  return useApiMutation(
    (accountName) => `/org/member/${encodeURIComponent(accountName)}`,
    'delete',
    {},
    {
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: queryKeys.orgMembers() });
        NotificationManager.success('Team member removed', '', 3000);
      },
      onError: (err) => {
        NotificationManager.error(
          err.message || 'Could not remove team member',
          '',
          4000,
        );
      },
    },
  );
}
